import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  BtnBarSpan,
  DangerAlert,
  Icon,
  InfoAlert,
  Link,
  SvgSpinner,
  SvgTrash,
  Table,
  TertiaryBtn,
  UploadDropZone,
  WaitingAlert,
} from '@tmapy/style-guide';

import { commonMessages } from '../../commonMessages';
import { FileIconWithBadge } from '../../docs/components/FileIconWithBadge';
import { useDownloadLink } from '../../hooks';
import type { ServerErrors } from '../../types';

import { UploadBtnWithDropZone } from './UploadBtnWithDropZone';

const msg = defineMessages({
  title: {
    id: 'orderAttachmentsSection.title',
    defaultMessage: 'Přílohy',
  },
  fileName: {
    id: 'orderAttachmentsSection.fileName',
    defaultMessage: 'Název souboru',
  },
  fileDate: {
    id: 'orderAttachmentsSection.fileDate',
    defaultMessage: 'Datum nahrání',
  },
  deleteFileBtn: {
    id: 'orderAttachmentsSection.deleteFileBtn',
    defaultMessage: 'Smazat',
  },
  deleteFileAlert: {
    id: 'orderAttachmentsSection.deleteFileAlert',
    defaultMessage: 'Opravdu chcete smazat přílohu?',
  },
  uploading: {
    id: 'orderAttachmentsSection.uploading',
    defaultMessage: 'Nahrávání...',
  },
  uploadFail: {
    id: 'orderAttachmentsSection.uploadFail',
    defaultMessage: 'Soubor {name} se nepodařilo nahrát.',
  },
  deleteFail: {
    id: 'orderAttachmentsSection.deleteFail',
    defaultMessage: 'Přílohu {name} se nepodařilo smazat.',
  },
  pendingUpload: {
    id: 'orderAttachmentsSection.pendingUpload',
    defaultMessage: 'Nahraje se po uložení',
  },
});

export type OrderAttachmentItem = {
  /** Id ze serveru, u čekající přílohy klientský klíč. */
  id: number | string;
  name: string;
  created?: string;
  /** Soubor zatím jen vybraný, na server odejde až s uložením entity. */
  isPending?: boolean;
};

type UploadingFile = { key: number; name: string; progress: number };

export type UploadProgressHandler = (event: { loaded: number; total?: number }) => void;

/** Podmnožina `CallApiResult`, kterou splní i soubor jen zařazený k nahrání. */
export type OrderAttachmentUploadResult =
  | { ok: true; data: OrderAttachmentItem }
  | { ok: false; error?: { data?: unknown } | null };

/** Antivirus i jiné odmítnutí souboru vrací backend u pole `file`. */
const getFileErrorMessage = (payload: unknown): string | undefined =>
  (payload as Partial<ServerErrors> | null)?.file?.[0]?.message;

type AttachmentRowProps = {
  downloadUrl: string | null;
  attachment: OrderAttachmentItem;
  isDeleteShown: boolean;
  isDeleting: boolean;
  isDisabled: boolean;
  onDelete(attachment: OrderAttachmentItem): void;
};

const AttachmentRow: React.FC<AttachmentRowProps> = ({
  downloadUrl,
  attachment,
  isDeleteShown,
  isDeleting,
  isDisabled,
  onDelete,
}) => {
  const formatMessage = useMessage();
  const downloadLink = useDownloadLink(downloadUrl ?? '', undefined, 'download_attachment');

  return (
    <tr>
      <td className='sg-a-w-0'>
        <FileIconWithBadge name={attachment.name} />
      </td>
      <td>
        {downloadUrl ? (
          <Link {...downloadLink} severity='colorful' download={attachment.name}>
            <div className='sg-a-ow-a'>{attachment.name}</div>
          </Link>
        ) : (
          <div className='sg-a-ow-a'>{attachment.name}</div>
        )}
      </td>
      <td className='sg-a-ta-r'>
        {attachment.isPending ? (
          <span className='sg-u-c-body-darkest'>{formatMessage(msg.pendingUpload)}</span>
        ) : (
          attachment.created && new Date(attachment.created).toLocaleString('cs-CZ')
        )}
      </td>
      {isDeleteShown && (
        <td className='sg-a-w-0'>
          {isDeleting ? (
            <Icon element={<SvgSpinner />} size='med' />
          ) : (
            <TertiaryBtn
              icon={{ element: <SvgTrash /> }}
              isDanger
              isDisabled={isDisabled}
              tooltip={formatMessage(msg.deleteFileBtn)}
              onClick={() => onDelete(attachment)}
            />
          )}
        </td>
      )}
    </tr>
  );
};

type OrderAttachmentsSectionProps = {
  /**
   * URL entity z API; příloha se stahuje z `<url>attachments/<id>/` přes token.
   * `null`, dokud entita neexistuje a nic se stáhnout nedá.
   */
  resourceUrl: string | null;
  attachments: OrderAttachmentItem[];
  /** Bez obsluhy sekce přílohy nepřidává, jen je ukazuje. */
  onUpload?(
    file: File,
    onUploadProgress: UploadProgressHandler,
  ): Promise<OrderAttachmentUploadResult>;
  /** Bez obsluhy sekce přílohy nemaže. */
  onDelete?(attachmentId: OrderAttachmentItem['id']): Promise<{ ok: boolean }>;
};

/**
 * Přílohy výdeje (objednávka dat, žádost bez exportu) ve stejné podobě jako přílohy přejímky.
 * `DocAttachments` použít nejde: přejímka má jiné endpointy, oprávnění i data příloh.
 */
export const OrderAttachmentsSection: React.FC<OrderAttachmentsSectionProps> = ({
  resourceUrl,
  attachments,
  onUpload,
  onDelete,
}) => {
  const canUpload = !!onUpload;
  const canDelete = !!onDelete;
  const formatMessage = useMessage();
  // Refetch detailu by znovu připojil rozpracovaný formulář na téže stránce a zahodil jeho
  // neuložené změny, proto se seznam doplňuje z odpovědí nahrání a smazání.
  const [items, setItems] = useState(attachments);
  useEffect(() => setItems(attachments), [attachments]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [deletingId, setDeletingId] = useState<OrderAttachmentItem['id'] | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const uploadHelpText = formatMessage.fallback(['orders.attachments.uploadHelpText']);
  const isWorking = uploadingFiles.length > 0 || deletingId !== null;

  const handleUpload = useCallback(
    async (e: React.SyntheticEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      const files = Array.from(input.files ?? []);
      input.value = '';
      if (!files.length || !onUpload) return;

      const queued = files.map((file, key) => ({ key, name: file.name, progress: 0 }));
      setUploadingFiles(queued);
      setErrorMessages([]);
      const failures: string[] = [];
      for (const [index, file] of files.entries()) {
        const response = await onUpload(file, ({ loaded, total }) =>
          setUploadingFiles((current) =>
            current.map((uploading) =>
              uploading.key === index
                ? { ...uploading, progress: total ? loaded / total : 0 }
                : uploading,
            ),
          ),
        );
        setUploadingFiles((current) => current.filter((uploading) => uploading.key !== index));
        if (response.ok) {
          const uploaded = response.data;
          // Rodič, který seznam řídí sám, ho mezitím mohl doplnit.
          setItems((current) =>
            current.some(({ id }) => id === uploaded.id) ? current : [...current, uploaded],
          );
        } else {
          const reason = getFileErrorMessage(response.error?.data);
          const failure = formatMessage(msg.uploadFail, { name: file.name });
          failures.push(reason ? `${failure} ${reason}` : failure);
        }
      }
      setErrorMessages(failures);
    },
    [formatMessage, onUpload],
  );

  const handleDelete = useCallback(
    async (attachment: OrderAttachmentItem) => {
      if (!onDelete || !window.confirm(formatMessage(msg.deleteFileAlert))) return;

      setDeletingId(attachment.id);
      setErrorMessages([]);
      const response = await onDelete(attachment.id);
      if (response.ok) {
        setItems((current) => current.filter(({ id }) => id !== attachment.id));
      } else {
        setErrorMessages([formatMessage(msg.deleteFail, { name: attachment.name })]);
      }
      setDeletingId(null);
    },
    [formatMessage, onDelete],
  );

  const hasFiles = items.length > 0 || uploadingFiles.length > 0;
  if (!hasFiles && !canUpload) return null;

  return (
    <section>
      <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
        <FormattedMessage {...msg.title} />
      </h3>
      <div className='sg-u-box sg-a-p-2 sg-u-vs-2'>
        {canUpload && uploadHelpText && <InfoAlert>{uploadHelpText}</InfoAlert>}
        {errorMessages.map((message) => (
          <DangerAlert key={message}>{message}</DangerAlert>
        ))}
        <UploadDropZone
          label={formatMessage(commonMessages.uploadDropZone)}
          btnLabel={formatMessage(commonMessages.uploadBtn)}
          isDisabled={!canUpload || isWorking}
          isShown={!hasFiles}
          name='files'
          onDrop={handleUpload}
        >
          <div className='sg-a-d-f sg-a-fd-c sg-a-ai-s sg-u-vs-2'>
            {hasFiles && (
              <div className='sg-a-mx--2'>
                <Table>
                  <thead>
                    <tr>
                      <th />
                      <th>
                        <FormattedMessage {...msg.fileName} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.fileDate} />
                      </th>
                      {canDelete && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((attachment) => (
                      <AttachmentRow
                        key={attachment.id}
                        downloadUrl={
                          resourceUrl && !attachment.isPending
                            ? `${resourceUrl}attachments/${attachment.id}/`
                            : null
                        }
                        attachment={attachment}
                        isDeleteShown={canDelete}
                        isDeleting={deletingId === attachment.id}
                        isDisabled={isWorking}
                        onDelete={handleDelete}
                      />
                    ))}
                    {uploadingFiles.map((uploading) => (
                      <tr key={uploading.key}>
                        <td />
                        <td>{uploading.name}</td>
                        <td colSpan={canDelete ? 2 : 1}>
                          <WaitingAlert>
                            {`${formatMessage(msg.uploading)} ${Math.round(uploading.progress * 100)}%`}
                          </WaitingAlert>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            {hasFiles && canUpload && (
              <BtnBar>
                <BtnBarSpan />
                <UploadBtnWithDropZone
                  label={formatMessage(commonMessages.uploadDropZone)}
                  btnLabel={formatMessage(commonMessages.uploadBtn)}
                  isDisabled={isWorking}
                  onChange={handleUpload}
                />
              </BtnBar>
            )}
          </div>
        </UploadDropZone>
      </div>
    </section>
  );
};
